import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { GetAllFollowUpTypes, CompletedUserTask } from "../Services/APIService";

/* ================= COLOR PALETTE ================= */
const COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#f59e0b",
  "#10b981",
  "#3b82f6",
  "#ef4444",
  "#14b8a6",
  "#f97316",
  "#06b6d4",
];

interface ChartTask {
  id: number;
  leadName: string;
  followupDate: string;
  createdOn: string;
}

interface ChartItem {
  name: string;
  count: number;
  color: string;
  tasks: ChartTask[];
}

/* ================= COMPONENT ================= */

const FollowupTypeLeadChart: React.FC = () => {
  const [chartData, setChartData] = useState<ChartItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /* ================= FETCH DATA ================= */

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setIsLoading(true);

        const followupTypes = await GetAllFollowUpTypes();
        const tasks = await CompletedUserTask();

        // Active follow-up types
        const activeTypes = followupTypes.filter(
          (t: any) => t.isActive && t.id !== 0 && t.name !== "Note"
        );

        // Today date range (FIXED – no mutation bug)
        const now = new Date();
        const startOfDay = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0,
          0,
          0,
          0
        );
        const endOfDay = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999
        );

        const todayTasks = tasks.filter((task: any) => {
          const date = new Date(task.followupDate);
          return date >= startOfDay && date <= endOfDay;
        });

        const mappedData: ChartItem[] = activeTypes.map(
          (type: any, index: number) => {
            const typeTasks = todayTasks.filter(
              (task: any) => task.followupTypeId === type.id
            );

            return {
              name: type.name,
              count: typeTasks.length,
              color: COLORS[index % COLORS.length],
              tasks: typeTasks.map((t: any) => ({
                id: t.id,
                leadName: t.leadName,
                followupDate: t.followupDate, // keep followupDate
                createdOn: t.createdOn, // ✅ add required field
              })),
            };
          }
        );

        setChartData(mappedData);
      } catch (error) {
        console.error("Error loading chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChartData();
  }, []);

  /* ================= FILTER ================= */

  const hiddenIndexes = [0];

  const filteredChartData = chartData.filter(
    (_, index) => !hiddenIndexes.includes(index)
  ); // hide zero bars

  const totalTasks = chartData.reduce((sum, item) => sum + item.count, 0);

  /* ================= TOOLTIP ================= */

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const { tasks, name, color } = payload[0].payload;

    return (
      <div
        className="bg-white border-0 rounded p-3 shadow"
        style={{ minWidth: "250px" }}
      >
        <div
          className="d-flex align-items-center mb-2 pb-2"
          style={{ borderBottom: `3px solid ${color}` }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: color,
              marginRight: 8,
            }}
          />
          <strong>{name}</strong>
          <span
            className="ms-auto badge rounded-pill"
            style={{ backgroundColor: color }}
          >
            {tasks.length}
          </span>
        </div>

        {tasks.length === 0 ? (
          <p className="text-muted small mb-0">No leads scheduled</p>
        ) : (
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {tasks.map((task: ChartTask) => (
              <div
                key={task.id}
                className="mb-2 p-2 rounded"
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                }}
              >
                <strong style={{ color }}>{task.leadName}</strong>
                <div className="small text-muted">
                  📅{" "}
                  {new Date(task.createdOn).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ================= LOADING ================= */

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="spinner-border" role="status" />
          <p className="mt-2">Loading chart...</p>
        </div>
      </div>
    );
  }

  /* ================= RENDER ================= */

  return (
    <div className="card border-0 shadow-sm">
      <div
        className="card-header border-0"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="text-white mb-1 fw-bold">📊 Today&#39;s Activity</h5>
            <p className="text-white-50 small mb-0">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
          <div className="badge bg-white text-primary px-3 py-2 rounded-pill">
            {totalTasks} {totalTasks === 1 ? "" : ""}
          </div>
        </div>
      </div>

      <div className="card-body p-4">
        {filteredChartData.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-inbox fs-1"></i>
            <p className="mt-2">No activity for today</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={filteredChartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" barSize={22} radius={[8, 8, 0, 0]}>
                {filteredChartData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default FollowupTypeLeadChart;
